import { supabase } from '@shared/lib/supabase';
import type { Provider } from '@supabase/supabase-js';

export type OAuthProvider = 'naver' | 'kakao' | 'google';

// 커스텀 OAuth (백엔드에서 처리)
const customOAuthProviders: OAuthProvider[] = ['naver'];

// Supabase에서 지원하는 프로바이더명으로 변환
const providerMap: Record<OAuthProvider, Provider> = {
  naver: 'naver' as Provider, // 실제로는 사용하지 않음
  kakao: 'kakao' as Provider,
  google: 'google',
};

/**
 * OAuth 로그인 시작
 * - 네이버: 백엔드를 통해 직접 OAuth 처리
 * - 카카오/구글: Supabase Auth 사용
 */
export async function signInWithOAuth(provider: OAuthProvider) {
  // 네이버는 백엔드에서 직접 처리
  if (customOAuthProviders.includes(provider)) {
    // 백엔드 OAuth 엔드포인트로 리다이렉트
    window.location.href = `/api/v1/auth/${provider}`;
    return { url: `/api/v1/auth/${provider}`, provider };
  }

  // 카카오, 구글은 Supabase 사용
  // 카카오는 account_email 등 개인정보 동의항목이 비즈 앱 전환 없이는 요청 자체가 불가하므로
  // scope을 프로필 정보로 제한 — 이름/이메일/휴대폰은 로그인 후 자체 폼(TermsAgreementPage)에서 직접 수집
  const scopes = provider === 'kakao' ? 'profile_nickname profile_image' : undefined;

  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: providerMap[provider],
    options: {
      redirectTo: `${window.location.origin}/auth/callback`,
      ...(scopes ? { scopes } : {}),
    },
  });

  if (error) {
    console.error('OAuth login error:', error);
    throw error;
  }

  return data;
}

/**
 * 로그아웃
 */
export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) {
    console.error('Sign out error:', error);
    throw error;
  }
}

/**
 * 현재 세션 가져오기
 */
export async function getSession() {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) {
    console.error('Get session error:', error);
    throw error;
  }
  return session;
}

/**
 * 현재 사용자 가져오기
 */
export async function getCurrentUser() {
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error) {
    console.error('Get user error:', error);
    throw error;
  }
  return user;
}
